import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptRequestAction,
  declineRequestAction,
  failedNotif,
  getNotifFromAction,
} from 'store/actions/notifActions';
import { NotificationRequestActionDialog } from '../NotificationRequestActionDialog';
import { openDetails } from 'store/actions/uiActions';
import { NotificationFromTable } from 'components/Tables/NotificationTable';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Loading } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { getFromMsg } from 'store/sagas/APIs/notification';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildNotificationListFilterChips,
  parseNotificationListFilterChipId,
} from 'utils/listTableActiveFilterChips';
import {
  NOTIFICATION_INVITE_TYPE_RECEIVED_FILTER,
  NOTIFICATION_STATUS_FILTER_OPTIONS,
  buildNotificationApiFilter,
  sanitizeNotificationSortOptions,
} from 'components/Tables/NotificationTable/notificationTableConstants';

const parseNotificationFromFilters = (searchParams, _paramKey, fallback) => ({
  selectedFromEmail: searchParams.get('fromEmail')
    ? searchParams.get('fromEmail').split(',').filter(Boolean)
    : fallback.selectedFromEmail,
  selectedToEmail: searchParams.get('toEmail')
    ? searchParams.get('toEmail').split(',').filter(Boolean)
    : fallback.selectedToEmail,
  selectedProperty: searchParams.get('property')
    ? searchParams.get('property').split(',').filter(Boolean)
    : fallback.selectedProperty,
  selectedEmailType: searchParams.get('emailStatus')
    ? searchParams.get('emailStatus').split(',').filter(Boolean)
    : fallback.selectedEmailType,
  selectedInviteType: searchParams.get('inviteType')
    ? searchParams.get('inviteType').split(',').filter(Boolean)
    : fallback.selectedInviteType,
});

const serializeNotificationFromFilters = (filters) => ({
  fromEmail:
    Array.isArray(filters.selectedFromEmail) && filters.selectedFromEmail.length > 0
      ? filters.selectedFromEmail.join(',')
      : '',
  toEmail:
    Array.isArray(filters.selectedToEmail) && filters.selectedToEmail.length > 0
      ? filters.selectedToEmail.join(',')
      : '',
  property:
    Array.isArray(filters.selectedProperty) && filters.selectedProperty.length > 0
      ? filters.selectedProperty.join(',')
      : '',
  emailStatus:
    Array.isArray(filters.selectedEmailType) && filters.selectedEmailType.length > 0
      ? filters.selectedEmailType.join(',')
      : '',
  inviteType:
    Array.isArray(filters.selectedInviteType) && filters.selectedInviteType.length > 0
      ? filters.selectedInviteType.join(',')
      : '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const EMAIL_TYPE = NOTIFICATION_STATUS_FILTER_OPTIONS;
const INVITE_TYPE = NOTIFICATION_INVITE_TYPE_RECEIVED_FILTER;

export const NotificationFrom = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedEmailType, setSelectedEmailType] = useState([]);
  const [selectedFromEmail, setSelectedFromEmail] = useState([]);
  const [selectedToEmail, setSelectedToEmail] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState([]);
  const [selectedInviteType, setSelectedInviteType] = useState([]);
  const [selectedEmailTypeDraft, setSelectedEmailTypeDraft] = useState([]);
  const [selectedFromEmailDraft, setSelectedFromEmailDraft] = useState([]);
  const [selectedToEmailDraft, setSelectedToEmailDraft] = useState([]);
  const [selectedPropertyDraft, setSelectedPropertyDraft] = useState([]);
  const [selectedInviteTypeDraft, setSelectedInviteTypeDraft] = useState([]);

  const [listFromEmail, setListFromEmail] = useState([]);
  const [listToEmail, setListToEmail] = useState([]);
  const [listProperty, setListProperty] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);

  const messages = useSelector((state) => state.getIn(['notification', 'msgFrom']));
  const messagesJSON = messages && messages.toJS();

  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      searchText: '',
      searchPlaceholder: 'Search Notification',
    },
    extraFiltersParam: 'receivedFilters',
    defaultExtraFilters: {
      selectedFromEmail: [],
      selectedToEmail: [],
      selectedProperty: [],
      selectedEmailType: [],
      selectedInviteType: [],
    },
    parseExtraFilters: parseNotificationFromFilters,
    serializeExtraFilters: serializeNotificationFromFilters,
  });

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(messagesJSON?.total) || 0,
    isLoading: Boolean(messagesJSON?.isLoading),
  });

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedFromEmail = Array.isArray(extraFilters.selectedFromEmail)
      ? extraFilters.selectedFromEmail
      : [];
    const nextSelectedToEmail = Array.isArray(extraFilters.selectedToEmail)
      ? extraFilters.selectedToEmail
      : [];
    const nextSelectedProperty = Array.isArray(extraFilters.selectedProperty)
      ? extraFilters.selectedProperty
      : [];
    const nextSelectedEmailType = Array.isArray(extraFilters.selectedEmailType)
      ? extraFilters.selectedEmailType
      : [];
    const nextSelectedInviteType = Array.isArray(extraFilters.selectedInviteType)
      ? extraFilters.selectedInviteType
      : [];
    setSelectedFromEmail((prev) =>
      areArraysEqual(prev, nextSelectedFromEmail) ? prev : nextSelectedFromEmail,
    );
    setSelectedToEmail((prev) =>
      areArraysEqual(prev, nextSelectedToEmail) ? prev : nextSelectedToEmail,
    );
    setSelectedProperty((prev) =>
      areArraysEqual(prev, nextSelectedProperty) ? prev : nextSelectedProperty,
    );
    setSelectedEmailType((prev) =>
      areArraysEqual(prev, nextSelectedEmailType) ? prev : nextSelectedEmailType,
    );
    setSelectedInviteType((prev) =>
      areArraysEqual(prev, nextSelectedInviteType) ? prev : nextSelectedInviteType,
    );
    setSelectedFromEmailDraft((prev) =>
      areArraysEqual(prev, nextSelectedFromEmail) ? prev : nextSelectedFromEmail,
    );
    setSelectedToEmailDraft((prev) =>
      areArraysEqual(prev, nextSelectedToEmail) ? prev : nextSelectedToEmail,
    );
    setSelectedPropertyDraft((prev) =>
      areArraysEqual(prev, nextSelectedProperty) ? prev : nextSelectedProperty,
    );
    setSelectedEmailTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedEmailType) ? prev : nextSelectedEmailType,
    );
    setSelectedInviteTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedInviteType) ? prev : nextSelectedInviteType,
    );
  }, [extraFilters]);

  useEffect(() => {
    if (messagesJSON?.data) {
      const fromEmails = Array.isArray(messagesJSON.fromEmails) ? messagesJSON.fromEmails : [];
      const toEmails = Array.isArray(messagesJSON.toEmails) ? messagesJSON.toEmails : [];
      const properties = Array.isArray(messagesJSON.properties) ? messagesJSON.properties : [];
      setListFromEmail(
        fromEmails.map((item) => ({ text: item.accepter_email, value: item.accepter_email })),
      );
      setListToEmail(
        toEmails.map((item) => ({ text: item.requestor_email, value: item.requestor_email })),
      );
      setListProperty(
        properties.map((item) => ({ text: item.unique_property, value: item.unique_property })),
      );
    }
  }, [messages]);

  const buildListFilter = useCallback(
    () =>
      buildNotificationApiFilter(filter, {
        accepterEmails: selectedFromEmail,
        requestorEmails: selectedToEmail,
        properties: selectedProperty,
        statuses: selectedEmailType,
        types: selectedInviteType,
      }),
    [
      filter,
      selectedFromEmail,
      selectedToEmail,
      selectedProperty,
      selectedEmailType,
      selectedInviteType,
    ],
  );

  const handleDispatch = useCallback(() => {
    dispatch(
      getNotifFromAction({
        filter: buildListFilter(),
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions: sanitizeNotificationSortOptions(sortOptions),
      }),
    );
  }, [
    dispatch,
    filter,
    selectedFromEmail,
    selectedToEmail,
    selectedProperty,
    selectedEmailType,
    selectedInviteType,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
    buildListFilter,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getFromMsg({
          filter: buildNotificationApiFilter(),
          offset: 0,
          limit: 1,
          sortOptions: sanitizeNotificationSortOptions(sortOptions),
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(messagesJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getFromMsg({
            filter: buildListFilter(),
            offset,
            limit,
            sortOptions: sanitizeNotificationSortOptions(sortOptions),
          }),
      });
    } catch (e) {
      throw new Error('Unable to export received notifications right now.');
    }
  }, [messagesJSON?.total, buildListFilter, sortOptions]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(messagesJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getFromMsg({
            filter: buildNotificationApiFilter(),
            offset,
            limit,
            sortOptions: sanitizeNotificationSortOptions(sortOptions),
          }),
      });
    } catch (e) {
      throw new Error('Unable to export full received notifications dataset right now.');
    }
  }, [fullExportDatasetTotal, messagesJSON?.total, sortOptions]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const onClickMessage = useCallback(
    (message) => {
      const requestId = message?.id;
      if (!requestId) {
        dispatch(failedNotif('Notification detail ID is missing.'));
        return;
      }
      dispatch(
        openDetails({
          type: 'message',
          data: message,
        }),
      );
      navigate(`/notification/received/${requestId}/detail`);
    },
    [dispatch, navigate],
  );

  const onClickAccept = useCallback((message) => {
    setPendingAction({ type: 'accept', message });
  }, []);

  const onClickDecline = useCallback((message) => {
    setPendingAction({ type: 'decline', message });
  }, []);

  const handleConfirmPendingAction = useCallback(
    (declineReason) => {
      if (!pendingAction?.message) return;
      const { type, message } = pendingAction;
      if (type === 'accept') {
        dispatch(
          acceptRequestAction({
            id: message.id,
            type: message.request_type,
          }),
        );
      } else {
        dispatch(
          declineRequestAction({
            id: message.id,
            text: declineReason,
          }),
        );
      }
      setPendingAction(null);
    },
    [dispatch, pendingAction],
  );

  const resetFunc = useCallback(() => {
    setSelectedEmailType([]);
    setSelectedFromEmail([]);
    setSelectedToEmail([]);
    setSelectedProperty([]);
    setSelectedInviteType([]);
    setSelectedEmailTypeDraft([]);
    setSelectedFromEmailDraft([]);
    setSelectedToEmailDraft([]);
    setSelectedPropertyDraft([]);
    setSelectedInviteTypeDraft([]);
    setExtraFilters({
      selectedFromEmail: [],
      selectedToEmail: [],
      selectedProperty: [],
      selectedEmailType: [],
      selectedInviteType: [],
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const applyFunc = useCallback(() => {
    setSelectedFromEmail(selectedFromEmailDraft);
    setSelectedToEmail(selectedToEmailDraft);
    setSelectedProperty(selectedPropertyDraft);
    setSelectedEmailType(selectedEmailTypeDraft);
    setSelectedInviteType(selectedInviteTypeDraft);
    setExtraFilters({
      selectedFromEmail: selectedFromEmailDraft,
      selectedToEmail: selectedToEmailDraft,
      selectedProperty: selectedPropertyDraft,
      selectedEmailType: selectedEmailTypeDraft,
      selectedInviteType: selectedInviteTypeDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedFromEmailDraft,
    selectedToEmailDraft,
    selectedPropertyDraft,
    selectedEmailTypeDraft,
    selectedInviteTypeDraft,
    setExtraFilters,
    setPagination,
  ]);

  const listFilterChips = useMemo(
    () =>
      buildNotificationListFilterChips({
        selectedFromEmail,
        selectedToEmail,
        selectedProperty,
        selectedEmailType,
        selectedInviteType,
        emailTypeOptions: EMAIL_TYPE,
        inviteTypeOptions: INVITE_TYPE,
      }),
    [selectedFromEmail, selectedToEmail, selectedProperty, selectedEmailType, selectedInviteType],
  );

  const handleRemoveNotificationListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseNotificationListFilterChipId(chipId);
      if (!parsed) return;
      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      if (parsed.type === 'fromEmail') {
        const next = selectedFromEmail.filter((s) => s !== parsed.value);
        setSelectedFromEmail(next);
        setSelectedFromEmailDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedFromEmail: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'toEmail') {
        const next = selectedToEmail.filter((s) => s !== parsed.value);
        setSelectedToEmail(next);
        setSelectedToEmailDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedToEmail: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'property') {
        const next = selectedProperty.filter((s) => s !== parsed.value);
        setSelectedProperty(next);
        setSelectedPropertyDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedProperty: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'emailType') {
        const next = selectedEmailType.filter((s) => s !== parsed.value);
        setSelectedEmailType(next);
        setSelectedEmailTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedEmailType: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'inviteType') {
        const next = selectedInviteType.filter((s) => s !== parsed.value);
        setSelectedInviteType(next);
        setSelectedInviteTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedInviteType: next }));
        bumpPage();
      }
    },
    [
      selectedFromEmail,
      selectedToEmail,
      selectedProperty,
      selectedEmailType,
      selectedInviteType,
      setExtraFilters,
      setPagination,
    ],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveNotificationListFilterChip,
    resetFunc,
  );

  const listContent = messagesJSON?.data ? (
    <React.Fragment>
      <CustomBox>
        {messagesJSON.isLoading && (
          <LoadingEffect>
            <Loading size={'large'} />
          </LoadingEffect>
        )}
      </CustomBox>
      <Box>
        <NotificationFromTable
          messages={messagesJSON.data}
          onRequestExportData={handleRequestExportData}
          onRequestFullExportData={handleRequestFullExportData}
          exportFullDatasetTotal={fullExportDatasetTotal}
          dropFilterActiveSummary={dropFilterActiveSummary}
          dropFilterActiveCount={dropFilterActiveCount}
          filter={filter}
          onChangeFilter={setFilter}
          pagination={{
            ...pagination,
            totalItems: messagesJSON.total,
            totalPages: Math.ceil(messagesJSON.total / pagination.rowsPerPage),
          }}
          dropFilter={{
            component: (
              <DropFilterContainer>
                <Dropdown
                  onChange={(e) => {
                    setSelectedToEmailDraft(e.target.value);
                  }}
                  options={listToEmail}
                  value={selectedToEmailDraft}
                  multiselect={true}
                  showSearch={true}
                  placeholder=""
                  placeholderFlag={true}
                  label="From"
                  style={{
                    width: '100%',
                  }}
                />

                <Dropdown
                  onChange={(e) => {
                    setSelectedFromEmailDraft(e.target.value);
                  }}
                  options={listFromEmail}
                  value={selectedFromEmailDraft}
                  multiselect={true}
                  showSearch={true}
                  placeholder=""
                  placeholderFlag={true}
                  label="To"
                  style={{
                    width: '100%',
                  }}
                />

                <Dropdown
                  onChange={(e) => {
                    setSelectedPropertyDraft(e.target.value);
                  }}
                  options={listProperty}
                  value={selectedPropertyDraft}
                  multiselect={true}
                  showSearch={true}
                  placeholder=""
                  placeholderFlag={true}
                  label="Property"
                  style={{
                    width: '100%',
                  }}
                />

                <Dropdown
                  onChange={(e) => {
                    setSelectedEmailTypeDraft(e.target.value);
                  }}
                  options={EMAIL_TYPE}
                  value={selectedEmailTypeDraft}
                  multiselect={true}
                  showSearch={true}
                  placeholder=""
                  placeholderFlag={true}
                  label="Status"
                  style={{
                    width: '100%',
                  }}
                />

                <Dropdown
                  onChange={(e) => {
                    setSelectedInviteTypeDraft(e.target.value);
                  }}
                  options={INVITE_TYPE}
                  value={selectedInviteTypeDraft}
                  multiselect={true}
                  showSearch={true}
                  placeholder=""
                  placeholderFlag={true}
                  label="Request Type"
                  style={{
                    width: '100%',
                  }}
                />
              </DropFilterContainer>
            ),
            resetFunc: resetFunc,
            applyFunc: applyFunc,
          }}
          onChangePagination={setPagination}
          sortOptions={sortOptions}
          onChangeSort={setSortOptions}
          onClickMessage={onClickMessage}
          onClickRow={onClickMessage}
          onClickAccept={onClickAccept}
          onClickDecline={onClickDecline}
        />
      </Box>
      <NotificationRequestActionDialog
        type={pendingAction?.type}
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmPendingAction}
      />
    </React.Fragment>
  ) : (
    <Loading size={'large'} />
  );

  if (embedded) return listContent;

  return <DashboardLayoutContainer>{listContent}</DashboardLayoutContainer>;
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: fixed;
  top: 45%;
  left: calc(50vw - 32px);
  z-index: 1;
`;
